/**
 * DocSyncManager — V2 port of V1 ``components/sync/DocSyncManager.tsx``
 * (May 25, 2026).
 *
 * The single dialog that owns the post-OAuth "Gestisci file" UX for
 * Google Drive / OneDrive / SharePoint / Notion. Two layered jobs:
 *
 *   1. **Pick** — invoke the native picker via ``useFilePicker``,
 *      persist the user's selection through ``selectTrackedFolders`` /
 *      ``selectTrackedFiles``.
 *   2. **Sync** — let the user choose WHICH of the tracked items to
 *      ingest this run, then POST ``start-ingestion`` with the scoped
 *      ``folder_ids`` / ``file_ids``.
 *
 * The selection model mirrors V1 exactly:
 *
 *   selectedFolderIds: Set<string>   // whole-folder sync
 *   selectedFileIds:   Set<string>   // standalone tracked files
 *   selectedChildIds:  Map<folderId, Set<fileId>>
 *                                    // explicit child picks when the
 *                                    // parent folder is NOT in
 *                                    // selectedFolderIds (so the user
 *                                    // can sync 3 files out of a 50-file
 *                                    // folder without "exploding" the
 *                                    // whole folder)
 *
 * Storage impact math runs entirely client-side from the ``new_size_bytes``
 * aggregate the BE pre-computes per folder — no per-file calls.
 */

import { isJobStillBusy } from "@/features/sync/state/syncStore";
import {
  type DriveProvider,
  type FolderContentFile,
  type TrackedFile,
  type TrackedFolder,
  cancelDriveSync,
  getDriveIngestionStatus,
  isJobConflict,
} from "@ando/core";
import {
  ActiveSyncJobBanner,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  cn,
  toast,
  Checkbox,
} from "@ando/ui";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  File as FileIcon,
  Folder,
  Loader2,
  Plus,
  RefreshCw,
  Trash2,
} from "lucide-react";
import * as React from "react";

import { useChatUserId } from "@/features/chat/hooks/useChatSession";
import {
  useDriveForceSync,
  useDriveSyncTrigger,
  useFolderContents,
  useRemoveTrackedFile,
  useRemoveTrackedFolder,
  useSelectTrackedFiles,
  useSelectTrackedFolders,
  useTrackedContent,
} from "@/features/sync/hooks/useDriveSync";
import { driveQueryKeys } from "@/features/sync/hooks/useDriveSync";
import { type PickedItem, useFilePicker } from "@/features/sync/hooks/useFilePicker";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";

// ──────────────────────────────────────────────────────────────────────
// Storage thresholds (V1 parity)
// ──────────────────────────────────────────────────────────────────────

const STORAGE_WARN_PCT = 80;
const STORAGE_CRIT_PCT = 95;

const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

// ──────────────────────────────────────────────────────────────────────
// Public API
// ──────────────────────────────────────────────────────────────────────

export interface DocSyncManagerProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly provider: DriveProvider;
  readonly connectionKey?: string;
  readonly accountLabel?: string;
  /** Optional storage cap (bytes) — used by the StorageImpactBar. */
  readonly storageLimitBytes?: number;
  /** Optional current usage (bytes) — same. */
  readonly storageUsedBytes?: number;
  readonly onSyncStarted?: (jobId: string | null) => void;
}

export const DocSyncManager: React.FC<DocSyncManagerProps> = ({
  open,
  onOpenChange,
  provider,
  connectionKey,
  accountLabel,
  storageLimitBytes,
  storageUsedBytes,
  onSyncStarted,
}) => {
  const { t } = useLingui();
  // ── Server data ───────────────────────────────────────────────────
  const tracked = useTrackedContent(provider, connectionKey);
  const filePicker = useFilePicker();
  const selectFolders = useSelectTrackedFolders();
  const selectFiles = useSelectTrackedFiles();
  const removeFolder = useRemoveTrackedFolder();
  const removeFile = useRemoveTrackedFile();
  const syncTrigger = useDriveSyncTrigger();
  const forceSyncMut = useDriveForceSync();
  const userId = useChatUserId();

  // ── Selection state ──────────────────────────────────────────────
  const [selectedFolderIds, setSelectedFolderIds] = React.useState<Set<string>>(() => new Set());
  const [selectedFileIds, setSelectedFileIds] = React.useState<Set<string>>(() => new Set());
  // Per-top-folder picks when the user wants a subset of the children
  // instead of "sync the whole folder". Split into ``files`` vs
  // ``folders`` (June 10, 2026) so ``handleSync`` can route nested
  // sub-folder ids into ``folderIds[]`` and child file ids into
  // ``fileIds[]`` — pre-fix everything was lumped into one ``fileIds``
  // bucket so a checked sub-folder was wire-shaped to the BE as a file
  // and silently dropped. Recursive ``ChildList`` writes to BOTH sets
  // depending on ``entry.is_folder`` discriminator.
  const [selectedChildIds, setSelectedChildIds] = React.useState<
    Map<string, { files: Set<string>; folders: Set<string> }>
  >(() => new Map());
  const [expandedFolders, setExpandedFolders] = React.useState<Set<string>>(() => new Set());
  // Expansion state for sub-folder nodes (depth > 0). Lifted here so a
  // ``ChildFolderNode`` unmount/remount (e.g. collapsing then re-expanding
  // the top-level parent) doesn't blow away the user's drill-in path —
  // pre-fix the local ``useState`` inside each node was lost on every
  // parent-collapse, forcing the user to re-walk the tree from scratch
  // (June 10, 2026 audit B2).
  const [expandedSubFolders, setExpandedSubFolders] = React.useState<Set<string>>(() => new Set());
  const toggleSubFolderExpanded = React.useCallback((folderId: string) => {
    setExpandedSubFolders((s) => {
      const next = new Set(s);
      next.has(folderId) ? next.delete(folderId) : next.add(folderId);
      return next;
    });
  }, []);
  // Single-dialog delete pattern (V1 parity — no nested AlertDialog).
  const [deleteTarget, setDeleteTarget] = React.useState<
    null | { kind: "folder"; id: string; name: string } | { kind: "file"; id: string; name: string }
  >(null);
  // Active-sync-job surface (June 10, 2026). Populated when a sync
  // trigger 409s with ``isJobConflict`` — the BE 409 body already
  // carries the running ``active_job_id`` + start timestamp, so we
  // extract those instead of round-tripping to ``/sync-status``. The
  // banner stays visible until either (a) the user cancels, or (b)
  // they close the drawer.
  const [activeJob, setActiveJob] = React.useState<{
    jobId: string;
    startedAt: string | null;
    /**
     * BE-side phase reflected on the banner:
     *   - ``"running"``    — initial 409 capture; show "Annulla sync"
     *     CTA enabled.
     *   - ``"cancelling"`` — operator clicked Annulla, Temporal cancel
     *     is in flight (5–30 s). CTA disabled + "Annullamento…" copy.
     *     Polled until the job leaves the cancelling/active states
     *     so a retry doesn't double-fire the 409 path (audit CRIT-3).
     */
    status: "running" | "cancelling";
  } | null>(null);

  // Pre-select non-synced folders + standalone files when the drawer
  // OPENS — V1 parity: synced files stay unchecked + dimmed.
  //
  // June 4 2026 — pre-fix this effect's deps were just the array
  // lengths, so after a successful sync the data refetched with the
  // SAME counts (same N files, just one flipped synced → not-synced)
  // and the effect never re-ran. Result: the just-synced file
  // remained ticked, the "1 file da sincronizzare" banner stayed on
  // screen, and re-clicking "Sincronizza" no-op'd. Also: the previous
  // exhaustive-deps eslint-disable hid the bug at lint time.
  //
  // The right model: gate the initial selection on TanStack's
  // ``dataUpdatedAt`` (monotonic ms timestamp that changes ONLY when a
  // fetch lands fresh data). The ref pins the last timestamp we've
  // initialised for, so:
  //   - First open while data is loading → effect skips, then re-runs
  //     when ``dataUpdatedAt`` flips from 0 → fetched-ms.
  //   - Opening with a STALE cache, then a background refetch arrives
  //     → ``dataUpdatedAt`` changes → we re-init off the fresh
  //     snapshot (reviewer #8 — locking the selection to a stale
  //     snapshot would mean the just-synced file stays ticked).
  //   - Manual user ticks happen AFTER init for that timestamp →
  //     subsequent re-renders see the same timestamp, no reset.
  //   - Drawer close resets the ref so the next open re-arms.
  const initialisedAtRef = React.useRef<number | null>(null);
  const dataUpdatedAt = tracked.dataUpdatedAt;
  React.useEffect(() => {
    if (!open) {
      initialisedAtRef.current = null;
      return;
    }
    if (!tracked.data || dataUpdatedAt === 0) return;
    if (initialisedAtRef.current === dataUpdatedAt) return;
    initialisedAtRef.current = dataUpdatedAt;
    const folders = new Set<string>();
    for (const f of tracked.data.folders) folders.add(f.id);
    const files = new Set<string>();
    for (const f of tracked.data.files) {
      if (f.status !== "synced") files.add(f.id);
    }
    setSelectedFolderIds(folders);
    setSelectedFileIds(files);
    setExpandedFolders(new Set(tracked.data.folders.map((f) => f.id)));
  }, [open, tracked.data, dataUpdatedAt]);

  // ── Picker → persist sequence ─────────────────────────────────────
  const handleAdd = async () => {
    try {
      const result = await filePicker.open({ provider, connectionKey });
      if (result.cancelled || result.items.length === 0) return;
      const folders = result.items.filter((i) => i.is_folder);
      const files = result.items.filter((i) => !i.is_folder);
      const promises: Promise<unknown>[] = [];
      if (folders.length > 0) {
        promises.push(
          selectFolders.mutateAsync({
            provider,
            connectionKey,
            selections: folders.map(toSelectFolder),
          }),
        );
      }
      if (files.length > 0) {
        promises.push(
          selectFiles.mutateAsync({
            provider,
            connectionKey,
            selections: files.map(toSelectFile),
          }),
        );
      }
      await Promise.allSettled(promises);
      const count = result.items.length;
      toast.success(
        count === 1
          ? t`${count} elemento aggiunto al tracking`
          : t`${count} elementi aggiunti al tracking`,
      );
      void tracked.refetch();
    } catch (err) {
      const reason = err instanceof Error ? err.message : t`errore`;
      toast.error(t`Impossibile aprire il picker: ${reason}`);
    }
  };

  // ── Sync trigger ──────────────────────────────────────────────────
  const handleSync = async () => {
    // Dedupe explicit child picks against whole-folder selections —
    // backend handles whole folders cheaper than enumerating their
    // children, so child ids ride only when the parent is NOT selected.
    // Files and sub-folders are routed to DISTINCT BE arrays:
    //   * ``fileIds[]``   — individual files to ingest one-shot.
    //   * ``folderIds[]`` — folders the BE expands recursively.
    // Pre-fix everything went through ``fileIds[]`` so a checked sub-
    // folder was shipped as a file id and silently dropped at the BE
    // (June 10, 2026 audit B1).
    const explicitChildFiles = new Set<string>();
    const explicitChildFolders = new Set<string>();
    for (const [topFolderId, picks] of selectedChildIds.entries()) {
      if (selectedFolderIds.has(topFolderId)) continue;
      for (const fid of picks.files) explicitChildFiles.add(fid);
      for (const fid of picks.folders) explicitChildFolders.add(fid);
    }
    const fileIds = Array.from(new Set([...selectedFileIds, ...explicitChildFiles]));
    const folderIds = Array.from(new Set([...selectedFolderIds, ...explicitChildFolders]));
    if (folderIds.length === 0 && fileIds.length === 0) {
      toast.error(t`Seleziona almeno un elemento da sincronizzare.`);
      return;
    }
    try {
      const res = await syncTrigger.mutateAsync({
        provider,
        connectionKey,
        folderIds,
        fileIds,
      });
      setActiveJob(null);
      toast.success(t`Sincronizzazione avviata`);
      onSyncStarted?.(res.job_id ?? null);
      onOpenChange(false);
    } catch (err) {
      // BE 409 with ``isJobConflict`` carries the running job in the
      // error body (``active_job_id`` + ``active_job_started_at``) — surface
      // it as an inline banner with a cancel CTA instead of a dead-end
      // toast. Operator can either annulla and retry OR wait. Per
      // June 10, 2026 feedback: "va gestito e fatto vedere ed
      // eventualmente ci vuole stop come già avviene in v1".
      if (isJobConflict(err)) {
        const data = (err as unknown as { data?: Record<string, unknown> }).data ?? {};
        const jobId = typeof data.active_job_id === "string" ? data.active_job_id : null;
        const startedAt =
          typeof data.active_job_started_at === "string" ? data.active_job_started_at : null;
        if (jobId) {
          setActiveJob({ jobId, startedAt, status: "running" });
          return;
        }
      }
      const reason = err instanceof Error ? err.message : t`errore`;
      toast.error(t`Impossibile avviare la sincronizzazione: ${reason}`);
    }
  };

  // ── Cancel the running sync job + propagation poll ────────────────
  //
  // Temporal cancellation is asynchronous: the BE acks the cancel
  // request immediately (HTTP 200) but the underlying workflow takes
  // 5–30 s to release. During that window the same ``job_id`` keeps
  // returning 409 on a retry. The poll below watches
  // ``getDriveIngestionStatus`` (the authoritative live-status
  // endpoint — pre-fix the loop hit ``/sync-status`` which exposes
  // ``last_sync_status``, NOT ``job_status``, so the loop always
  // exited on iteration 1 with a false-success toast: audit ciclo 3
  // CRIT-WD1) until the BE ``status`` leaves the still-busy set
  // (shared predicate ``isJobStillBusy`` from ``syncStore`` — single
  // source of truth, audit WARN-D2). Wired through an ``AbortController``
  // so closing the modal mid-poll cancels the loop, aborts the in-
  // flight fetch, and prevents the toast/setState/invalidate fan-out
  // from firing on an unmounted component (audit CRIT-M1).
  const queryClient = useQueryClient();
  const pollAbortRef = React.useRef<AbortController | null>(null);

  // June 11, 2026 — counter live delle query in flight con prefix
  // ``driveQueryKeys.all``. Sync col fetcher: appena l'invalidate
  // triggera i refetch parte > 0; quando tutte le query risolvono
  // torna 0. Usato dal pulsante "Aggiorna" per spinner + disabled
  // (no double-click race, feedback "sto facendo qualcosa").
  const isRefreshing = useIsFetching({ queryKey: driveQueryKeys.all }) > 0;

  // Tear down the in-flight cancel-poll on unmount OR on drawer close.
  // Both branches abort the same controller, so the loop's
  // ``signal.aborted`` checks cleanly bail without a stray toast.
  React.useEffect(() => {
    return () => {
      pollAbortRef.current?.abort();
      pollAbortRef.current = null;
    };
  }, []);

  const handleCancelActiveJob = React.useCallback(async () => {
    if (!activeJob || !userId) return;
    // Supersede any prior cancel-poll still in flight (double-click
    // / race guard) BEFORE flipping the banner status so the operator
    // never sees a stale "cancelling" surface from a previous attempt.
    pollAbortRef.current?.abort();
    const controller = new AbortController();
    pollAbortRef.current = controller;
    const signal = controller.signal;

    setActiveJob((prev) => (prev ? { ...prev, status: "cancelling" } : prev));
    try {
      await cancelDriveSync(activeJob.jobId, userId);
    } catch (err) {
      if (signal.aborted) return;
      // Cancel request itself failed — surface and roll back to
      // ``running`` so the operator can retry the cancel.
      setActiveJob((prev) => (prev ? { ...prev, status: "running" } : prev));
      const reason = err instanceof Error ? err.message : t`errore`;
      toast.error(t`Impossibile annullare: ${reason}`);
      return;
    }

    // Poll status until the BE flips the job out of the still-busy
    // set. Time-boxed to 30 s — beyond that the operator gets a soft
    // "più lento del previsto" toast and we clear the banner anyway
    // so the modal isn't stuck.
    const POLL_INTERVAL_MS = 1500;
    const POLL_DEADLINE_MS = 30_000;
    const startedAt = Date.now();
    let settled = false;
    while (!settled && !signal.aborted && Date.now() - startedAt < POLL_DEADLINE_MS) {
      await new Promise<void>((resolve) => {
        const t = setTimeout(resolve, POLL_INTERVAL_MS);
        signal.addEventListener(
          "abort",
          () => {
            clearTimeout(t);
            resolve();
          },
          { once: true },
        );
      });
      if (signal.aborted) return;
      try {
        const status = await getDriveIngestionStatus(activeJob.jobId, userId, provider, { signal });
        if (signal.aborted) return;
        if (!isJobStillBusy(status.status)) {
          settled = true;
        }
      } catch (err) {
        if (signal.aborted || (err as Error)?.name === "AbortError") return;
        // Transient poll error — keep trying until the deadline.
      }
    }

    if (signal.aborted) return;
    setActiveJob(null);
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: driveQueryKeys.trackedContent(userId, provider, connectionKey),
      }),
      queryClient.invalidateQueries({
        queryKey: driveQueryKeys.status(userId, provider, connectionKey),
      }),
    ]);
    if (settled) {
      toast.success(t`Sincronizzazione annullata — puoi riprovare`);
    } else {
      toast.info(t`Annullamento più lento del previsto — riprova fra qualche istante.`);
    }
  }, [activeJob, userId, queryClient, provider, connectionKey, t]);

  // Clear the active-job banner when the drawer closes so a re-open
  // doesn't show a stale 409 surface (the operator may have cancelled
  // via another shell, or the job may have completed). Also abort
  // any in-flight cancel-poll so toasts don't fire after close.
  React.useEffect(() => {
    if (!open) {
      pollAbortRef.current?.abort();
      pollAbortRef.current = null;
      setActiveJob(null);
    }
  }, [open]);

  // ── Delete confirmation handlers ──────────────────────────────────
  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      if (deleteTarget.kind === "folder") {
        await removeFolder.mutateAsync({
          id: deleteTarget.id,
          provider,
          connectionKey,
        });
      } else {
        await removeFile.mutateAsync({
          id: deleteTarget.id,
          provider,
          connectionKey,
        });
      }
      toast.success(t`"${deleteTarget.name}" rimosso dal tracking`);
      setDeleteTarget(null);
      void tracked.refetch();
    } catch (err) {
      const reason = err instanceof Error ? err.message : t`errore`;
      toast.error(t`Rimozione non riuscita: ${reason}`);
    }
  };

  // ── Storage impact math ───────────────────────────────────────────
  const projectedBytes = React.useMemo(() => {
    if (!tracked.data) return 0;
    let total = 0;
    for (const folder of tracked.data.folders) {
      const counts = folder.file_counts;
      if (!counts) continue;
      const folderSize = counts.new_count > 0 ? counts.new_count * 0 : 0;
      // V1's BE returns ``new_size_bytes`` on the folder-contents probe;
      // until that probe runs we use 0 as a conservative lower bound.
      // The dedicated useFolderContents hook below upgrades the
      // estimate per-folder as the data lands.
      if (selectedFolderIds.has(folder.id)) {
        total += folderSize;
      }
    }
    for (const file of tracked.data.files) {
      if (selectedFileIds.has(file.id)) {
        total += file.size_bytes ?? 0;
      }
    }
    return total;
  }, [tracked.data, selectedFolderIds, selectedFileIds]);

  const totalSelectable = (tracked.data?.folders.length ?? 0) + (tracked.data?.files.length ?? 0);
  // June 10, 2026 — count picks ACROSS the whole tree, not just the
  // top-level rows. Pre-fix: a user checking a sub-folder (or sub-file)
  // without ticking the ancestor top-level folder saw the CTA stay
  // disabled with "Seleziona elementi da sincronizzare" because the
  // sub-tree picks were stored in ``selectedChildIds`` and ignored
  // here. The handleSync routing already consumes both surfaces — the
  // gate count had drifted from the wire payload.
  const totalSelected = React.useMemo(() => {
    let n = selectedFolderIds.size + selectedFileIds.size;
    for (const [topFolderId, picks] of selectedChildIds.entries()) {
      // When the top-level folder is selected, its descendants ride
      // the whole-folder ingestion path — don't double-count.
      if (selectedFolderIds.has(topFolderId)) continue;
      n += picks.files.size + picks.folders.size;
    }
    return n;
  }, [selectedFolderIds, selectedFileIds, selectedChildIds]);

  // ── Delta counts across all tracked folders + files ──────────────
  // Mirrors V1's ``DeltaSummaryBanner`` math: sum new/modified/synced
  // per folder + slot each standalone file into the right bucket
  // based on its ``status``. Drives the "Tutto sincronizzato" banner.
  const { newCount, modifiedCount, syncedCount } = React.useMemo(() => {
    let nC = 0;
    let mC = 0;
    let sC = 0;
    if (tracked.data) {
      for (const f of tracked.data.folders) {
        const c = f.file_counts;
        if (!c) continue;
        nC += c.new_count;
        mC += c.modified_count;
        sC += c.synced_count;
      }
      for (const file of tracked.data.files) {
        if (file.status === "synced") sC += 1;
        else if (file.status === "modified") mC += 1;
        else nC += 1;
      }
    }
    return { newCount: nC, modifiedCount: mC, syncedCount: sC };
  }, [tracked.data]);

  const handleForceFullResync = async () => {
    try {
      const res = await forceSyncMut.mutateAsync({
        provider,
        connectionKey,
      });
      toast.success(t`Ri-sincronizzazione completa avviata`);
      onSyncStarted?.(res.job_id ?? null);
      onOpenChange(false);
    } catch (err) {
      const reason = err instanceof Error ? err.message : t`errore`;
      toast.error(t`Impossibile avviare la ri-sincronizzazione: ${reason}`);
    }
  };

  const isLoading = tracked.isLoading;
  const isEmpty = !!tracked.data && totalSelectable === 0;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        side="right"
        ariaTitle={t`Gestisci file da sincronizzare`}
        className="flex w-[min(540px,95vw)] flex-col sm:max-w-none"
      >
        <DrawerHeader>
          <div className="flex items-center gap-2">
            <span className="grid h-8 w-8 place-items-center rounded-md bg-ando-orange-soft text-ando-orange">
              <Folder className="h-4 w-4" strokeWidth={2} />
            </span>
            <div className="min-w-0 flex-1">
              <h2 className="truncate font-display font-semibold text-[15px] text-ink-1">
                <Trans>Gestisci file</Trans>
              </h2>
              <p className="truncate text-[11.5px] text-ink-3">
                <Trans>
                  File e cartelle tracciati. Seleziona cosa sincronizzare o aggiungine altri.
                </Trans>
                {accountLabel ? ` · ${accountLabel}` : ""}
              </p>
            </div>
            <button
              type="button"
              onClick={() => void handleAdd()}
              disabled={filePicker.isOpening}
              className={cn(
                "inline-flex shrink-0 items-center gap-1 rounded-md bg-ando-orange px-2.5 py-1 font-medium text-[12px] text-white",
                "hover:bg-ando-orange/90 disabled:cursor-not-allowed disabled:opacity-50",
              )}
            >
              {filePicker.isOpening ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Plus className="h-3 w-3" />
              )}
              <Trans>Aggiungi</Trans>
            </button>
          </div>
        </DrawerHeader>

        <DrawerBody className="flex-1 overflow-y-auto px-5 py-4">
          {deleteTarget ? (
            <DeleteConfirm
              target={deleteTarget}
              busy={removeFolder.isPending || removeFile.isPending}
              onCancel={() => setDeleteTarget(null)}
              onConfirm={() => void confirmDelete()}
            />
          ) : isLoading ? (
            <p className="text-[12.5px] text-ink-3">
              <Trans>Carico i tuoi file…</Trans>
            </p>
          ) : isEmpty ? (
            <EmptyState onAdd={handleAdd} busy={filePicker.isOpening} />
          ) : (
            <div className="flex flex-col gap-4">
              {/* ── Active sync job (409 conflict surface) ─────── */}
              {/* The modal is already scoped to a single connection so
                  the per-banner ``connectionLabel`` would be redundant
                  noise (audit WARN-1). ``id`` lets the disabled
                  "Sincronizza" CTA below ``aria-describedby`` this
                  banner so AT users hear "Annulla la sync in corso
                  sopra per ritentare" (audit SUGG-4). */}
              {activeJob ? (
                <ActiveSyncJobBanner
                  id="active-sync-job-banner"
                  startedAt={activeJob.startedAt}
                  status={activeJob.status}
                  onCancel={handleCancelActiveJob}
                />
              ) : null}

              {/* ── Delta summary banner ──────────────────────── */}
              <DeltaSummaryBanner
                totalSelected={totalSelected}
                newCount={newCount}
                modifiedCount={modifiedCount}
                syncedCount={syncedCount}
                onForceFullResync={() => void handleForceFullResync()}
              />

              {/* ── Folder list (WAI-ARIA treeview root) ───────── */}
              {/* ``role="tree"`` groups the descendant ``role="treeitem"``
                  rows so AT announces "tree, X items" instead of
                  "list". Keyboard-nav (ArrowDown/Right roving tabindex)
                  is a planned follow-up; the tree role already gives
                  screen-reader users the hierarchy. */}
              {tracked.data && tracked.data.folders.length > 0 ? (
                <div
                  role="tree"
                  aria-label={t`File e cartelle tracciati`}
                  className="flex flex-col gap-2"
                >
                  {tracked.data.folders.map((folder) => (
                    <FolderRowComponent
                      key={folder.id}
                      folder={folder}
                      provider={provider}
                      connectionKey={connectionKey}
                      expanded={expandedFolders.has(folder.id)}
                      selected={selectedFolderIds.has(folder.id)}
                      selectedChildIds={selectedChildIds.get(folder.id)}
                      onToggleExpanded={() =>
                        setExpandedFolders((s) => {
                          const next = new Set(s);
                          next.has(folder.id) ? next.delete(folder.id) : next.add(folder.id);
                          return next;
                        })
                      }
                      onToggleSelected={() =>
                        setSelectedFolderIds((s) => {
                          const next = new Set(s);
                          next.has(folder.id) ? next.delete(folder.id) : next.add(folder.id);
                          return next;
                        })
                      }
                      onToggleChild={(id, isFolder) =>
                        setSelectedChildIds((s) => {
                          const next = new Map(s);
                          const prev = next.get(folder.id);
                          const files = new Set(prev?.files ?? []);
                          const folders = new Set(prev?.folders ?? []);
                          const bucket = isFolder ? folders : files;
                          bucket.has(id) ? bucket.delete(id) : bucket.add(id);
                          next.set(folder.id, { files, folders });
                          return next;
                        })
                      }
                      expandedSubFolders={expandedSubFolders}
                      onToggleSubFolderExpanded={toggleSubFolderExpanded}
                      onDelete={() =>
                        setDeleteTarget({
                          kind: "folder",
                          id: folder.id,
                          name: folder.name,
                        })
                      }
                    />
                  ))}
                </div>
              ) : null}

              {/* ── Standalone files ──────────────────────────── */}
              {tracked.data && tracked.data.files.length > 0 ? (
                <section className="flex flex-col gap-1.5">
                  <SectionLabel>
                    <Trans>File singoli</Trans>
                  </SectionLabel>
                  {tracked.data.files.map((file) => (
                    <StandaloneFileRow
                      key={file.id}
                      file={file}
                      selected={selectedFileIds.has(file.id)}
                      onToggle={() =>
                        setSelectedFileIds((s) => {
                          const next = new Set(s);
                          next.has(file.id) ? next.delete(file.id) : next.add(file.id);
                          return next;
                        })
                      }
                      onDelete={() =>
                        setDeleteTarget({
                          kind: "file",
                          id: file.id,
                          name: file.name,
                        })
                      }
                    />
                  ))}
                </section>
              ) : null}

              {/* ── Storage impact (when caps are known) ──────── */}
              {storageLimitBytes && storageUsedBytes !== undefined ? (
                <StorageImpactBar
                  used={storageUsedBytes}
                  projected={projectedBytes}
                  limit={storageLimitBytes}
                />
              ) : null}
            </div>
          )}
        </DrawerBody>

        <DrawerFooter className="flex flex-col gap-2">
          <button
            type="button"
            // June 11, 2026 — pre-fix ``tracked.refetch()`` rinfrescava
            // solo le folder ROOT + standalone files. Tutte le sub-folder
            // espanse usano ``useFolderContents(folderId)`` con queryKey
            // separata (``["driveManagement","folderContents", id]``) +
            // staleTime di 15s → l'utente cliccava Aggiorna ma i nuovi
            // file dentro le sub-folder NON apparivano (es. dopo
            // l'estensione MIME whitelist per i CAD). Fix: invalida
            // l'INTERA subtree ``driveQueryKeys.all`` così tracked,
            // folder-contents, status e folder-tree si rifetchano tutti.
            // ``refetchType: "active"`` evita di triggerare le query
            // smontate (es. una sub-folder collapsed nel mentre).
            //
            // Loading state (giugno 11, 2026): l'utente vede lo spinner
            // mentre ``isFetchingDrive > 0`` → no double-click race +
            // feedback "sto facendo qualcosa". ``isFetching`` è un
            // counter delle query active in flight matching la
            // queryKey, sync con il fetcher.
            onClick={() => {
              void queryClient.invalidateQueries({
                queryKey: driveQueryKeys.all,
                refetchType: "active",
              });
            }}
            disabled={isRefreshing}
            aria-busy={isRefreshing}
            className="inline-flex items-center justify-center gap-1.5 self-start rounded-md px-2 py-1 text-[11.5px] text-ink-3 transition-colors hover:bg-black/[0.04] disabled:cursor-not-allowed disabled:opacity-60"
          >
            <RefreshCw className={cn("h-3 w-3", isRefreshing && "animate-spin")} />
            {isRefreshing ? <Trans>Aggiornamento…</Trans> : <Trans>Aggiorna</Trans>}
          </button>
          <button
            type="button"
            disabled={totalSelected === 0 || syncTrigger.isPending || activeJob !== null}
            // When the CTA is gated by the active-job banner, point AT
            // users at the banner via ``aria-describedby`` so the
            // disabled-button announcement carries the recovery
            // instructions (audit SUGG-4).
            aria-describedby={activeJob !== null ? "active-sync-job-banner" : undefined}
            onClick={() => void handleSync()}
            className={cn(
              "inline-flex w-full flex-wrap items-center justify-center gap-1.5 rounded-md px-3 py-2 font-medium text-[13px]",
              "bg-ando-orange text-white hover:bg-ando-orange/90",
              "disabled:cursor-not-allowed disabled:bg-ink-3/30 disabled:text-ink-3",
            )}
          >
            {syncTrigger.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" />
            ) : null}
            {/* WARN-3: copy compatta che resta su una riga anche a 320 px. */}
            {activeJob !== null ? (
              <Trans>Sync in corso · annulla sopra</Trans>
            ) : syncTrigger.isPending ? (
              <Trans>Sincronizzando…</Trans>
            ) : totalSelected === 0 ? (
              <Trans>Seleziona elementi da sincronizzare</Trans>
            ) : (
              <Plural
                value={totalSelected}
                one="Sincronizza # elemento"
                other="Sincronizza # elementi"
              />
            )}
          </button>
        </DrawerFooter>
      </DrawerContent>
      {/* Modal-based pickers (OneDrive / SharePoint / Notion) mount as a
          sibling — Radix Drawer manages its own focus trap; nesting
          another Drawer inside the same ``<Drawer>`` root would create
          a competing trap. The ``modalElement`` JSX itself is a Radix
          Drawer with its own ``<Drawer>`` wrapper, so it portals
          independently. The Google Picker uses a native iframe and
          doesn't render here. */}
      {filePicker.modalElement}
    </Drawer>
  );
};
DocSyncManager.displayName = "DocSyncManager";

// ──────────────────────────────────────────────────────────────────────
// Sub-components
// ──────────────────────────────────────────────────────────────────────

const SectionLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="font-semibold text-[10.5px] text-ink-2 uppercase tracking-[0.08em]">
    {children}
  </span>
);

/**
 * V1 parity: the banner splits the selection by delta status so the user
 * can read at a glance "I'm about to sync 3 new + 2 modified + 4 already-
 * synced docs". When every tracked file is up-to-date the banner flips
 * to "Tutto sincronizzato" and surfaces a "Ri-sincronizza tutto" CTA.
 */
const DeltaSummaryBanner: React.FC<{
  totalSelected: number;
  newCount: number;
  modifiedCount: number;
  syncedCount: number;
  onForceFullResync?: () => void;
}> = ({ totalSelected, newCount, modifiedCount, syncedCount, onForceFullResync }) => {
  const { t } = useLingui();
  const actionable = newCount + modifiedCount;

  if (totalSelected > 0 && actionable === 0 && syncedCount > 0) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-success/40 bg-success/[0.08] px-3 py-2 text-[12px] text-success">
        <CheckCircle2 className="h-3.5 w-3.5" />
        <span className="flex-1">
          <strong>
            <Trans>Tutto sincronizzato.</Trans>
          </strong>{" "}
          <span className="text-ink-3">
            <Trans>{syncedCount} file aggiornati. Puoi forzare una ri-sincronizzazione.</Trans>
          </span>
        </span>
        {onForceFullResync ? (
          <button
            type="button"
            onClick={onForceFullResync}
            className="rounded-md border border-success/40 bg-white/70 px-2 py-0.5 font-medium text-[11px] text-success hover:bg-white"
          >
            <Trans>Ri-sincronizza</Trans>
          </button>
        ) : null}
      </div>
    );
  }

  if (totalSelected === 0) {
    return (
      <div className="rounded-lg border border-hairline bg-cream/40 px-3 py-2 text-[12px] text-ink-3">
        <Trans>Seleziona almeno una cartella o un file da sincronizzare.</Trans>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-hairline border-l-2 border-l-ando-orange/55 bg-card/60 px-3 py-2 text-[12px] text-ink-1">
      <p>
        <strong className="text-ando-orange">{actionable || totalSelected}</strong>{" "}
        <Trans>file da sincronizzare</Trans>
      </p>
      {newCount + modifiedCount + syncedCount > 0 ? (
        <p className="mt-0.5 text-[11px] text-ink-3 tabular-nums">
          {newCount > 0 ? t`${newCount} nuovi` : null}
          {newCount > 0 && modifiedCount > 0 ? " · " : null}
          {modifiedCount > 0 ? t`${modifiedCount} modificati` : null}
          {(newCount > 0 || modifiedCount > 0) && syncedCount > 0 ? " · " : null}
          {syncedCount > 0 ? t`${syncedCount} già sincronizzati` : null}
        </p>
      ) : null}
    </div>
  );
};

/**
 * Per-top-folder child selection state. ``files`` and ``folders`` are
 * tracked SEPARATELY so ``handleSync`` can route them into the matching
 * BE buckets (``fileIds[]`` vs ``folderIds[]``). Pre-fix everything went
 * into a single Set and a checked sub-folder was wire-shaped as a file.
 */
interface ChildPicks {
  readonly files: Set<string>;
  readonly folders: Set<string>;
}

/**
 * IndeterminateCheckbox — tri-state checkbox. The HTML ``checked`` attr
 * is binary; ``indeterminate`` is a property-only flag set via JS that
 * renders the "—" glyph used by Finder/Drive when a folder has SOME
 * (but not all) children selected. ``ref`` sync keeps it in lockstep
 * with React state — declarative-looking from the caller's POV
 * (audit A3, June 10, 2026).
 */
const IndeterminateCheckbox: React.FC<
  Omit<React.InputHTMLAttributes<HTMLInputElement>, "type" | "ref"> & {
    readonly indeterminate?: boolean;
  }
> = (props) => <Checkbox {...props} />;

const FolderRowComponent: React.FC<{
  folder: TrackedFolder;
  provider: DriveProvider;
  connectionKey?: string;
  expanded: boolean;
  selected: boolean;
  selectedChildIds: ChildPicks | undefined;
  expandedSubFolders: Set<string>;
  onToggleExpanded: () => void;
  onToggleSelected: () => void;
  onToggleChild: (id: string, isFolder: boolean) => void;
  onToggleSubFolderExpanded: (folderId: string) => void;
  onDelete: () => void;
}> = ({
  folder,
  provider,
  connectionKey,
  expanded,
  selected,
  selectedChildIds,
  expandedSubFolders,
  onToggleExpanded,
  onToggleSelected,
  onToggleChild,
  onToggleSubFolderExpanded,
  onDelete,
}) => {
  const { t } = useLingui();
  // June 5 2026 — pass provider + connectionKey to ``useFolderContents``.
  // The BE requires both as query params; before the fix the call was
  // 422 ``Unprocessable Entity`` so every expand stayed on "Carico
  // contenuti…" forever (V1 ``host.ts:folderContents`` already passed
  // these — the bug was V2 dropping them at the FE boundary).
  const contents = useFolderContents(expanded ? folder.id : null, provider, connectionKey);
  const counts = folder.file_counts;
  // Tri-state: indeterminate when the user has manually picked SOME
  // children (file or sub-folder) without selecting the whole top-level
  // folder. Mirrors Finder/Drive — at a glance the operator sees that
  // a folder has partial picks without expanding it.
  const hasPartialPicks =
    !selected &&
    Boolean(
      selectedChildIds && (selectedChildIds.files.size > 0 || selectedChildIds.folders.size > 0),
    );

  return (
    <div
      role="treeitem"
      aria-expanded={expanded}
      aria-level={1}
      aria-selected={selected || undefined}
      className="rounded-lg border border-hairline bg-card/50"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2">
        <IndeterminateCheckbox
          checked={selected}
          indeterminate={hasPartialPicks}
          onChange={onToggleSelected}
          aria-label={t`Sincronizza ${folder.name}`}
        />
        <button
          type="button"
          onClick={onToggleExpanded}
          aria-label={expanded ? t`Comprimi` : t`Espandi`}
          aria-expanded={expanded}
          className="inline-flex h-5 w-5 items-center justify-center text-ink-3 hover:text-ink-1"
        >
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
        </button>
        <Folder className="h-4 w-4 text-ink-3" strokeWidth={1.75} />
        <span className="min-w-0 flex-1 truncate font-medium text-[12.5px] text-ink-1">
          {folder.name}
        </span>
        {counts ? (
          <div className="flex shrink-0 gap-1">
            {counts.new_count > 0 ? (
              <DeltaChip tone="new">
                <Trans>{counts.new_count} nuovi</Trans>
              </DeltaChip>
            ) : null}
            {counts.modified_count > 0 ? (
              <DeltaChip tone="modified">
                <Trans>{counts.modified_count} modificati</Trans>
              </DeltaChip>
            ) : null}
            {counts.synced_count > 0 ? (
              <DeltaChip tone="synced">{counts.synced_count} ✓</DeltaChip>
            ) : null}
          </div>
        ) : null}
        <button
          type="button"
          aria-label={t`Rimuovi ${folder.name}`}
          onClick={onDelete}
          className="grid h-6 w-6 place-items-center rounded-md text-ink-3 hover:bg-rose-50 hover:text-rose-500"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      </div>
      {/* Expanded children — file/subfolder tree, recursive. */}
      {expanded ? (
        <div className="border-hairline border-t px-3 py-2">
          {contents.isLoading ? (
            <p className="text-[11.5px] text-ink-3">
              <Trans>Carico contenuti…</Trans>
            </p>
          ) : contents.data && contents.data.files.length > 0 ? (
            // June 5 2026 — BE emits a single ``files[]`` with a
            // ``sync_status`` discriminator on each entry, NOT three
            // separate buckets. ``ChildList`` (refactored June 10,
            // 2026) splits the entries into folder vs file rows and
            // lazy-renders each subfolder via a recursive
            // ``ChildFolderNode`` so the user can drill into the full
            // tree — previously the "Tech" subfolder was rendered as a
            // flat file row with a "Nuovo" badge and there was no way
            // to inspect its contents (June 10 user feedback).
            <ChildList
              entries={contents.data.files}
              folderSelected={selected}
              selectedChildIds={selectedChildIds}
              onToggleChild={onToggleChild}
              expandedSubFolders={expandedSubFolders}
              onToggleSubFolderExpanded={onToggleSubFolderExpanded}
              provider={provider}
              connectionKey={connectionKey}
              depth={0}
            />
          ) : (
            <p className="text-[11.5px] text-ink-3">
              <Trans>Nessun contenuto.</Trans>
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
};

/**
 * ChildList — recursive renderer for the contents of a folder.
 *
 * Splits each entry by ``is_folder`` and dispatches to either a flat
 * file row OR a ``ChildFolderNode`` (expandable, lazy-loads its own
 * children via ``useFolderContents``). The tree expands on demand —
 * a collapsed subfolder makes no BE call, so deep / wide trees stay
 * cheap until the user actually drills in.
 *
 * ``folderSelected`` flows down ``unchanged`` because selecting the
 * topmost ``TrackedFolder`` is semantically "sync everything under
 * here" — every descendant (recursively) is conceptually checked and
 * the individual checkboxes render disabled to make the implicit
 * inclusion legible. Per-file overrides via ``selectedChildIds`` only
 * matter when the top-level folder is NOT selected.
 *
 * ``depth`` drives a small left-margin so nested levels read as a
 * tree without an explicit guide line — same visual rhythm V1's
 * ``DocSyncManager`` used.
 */
const ChildList: React.FC<{
  entries: readonly FolderContentFile[];
  folderSelected: boolean;
  selectedChildIds: ChildPicks | undefined;
  onToggleChild: (id: string, isFolder: boolean) => void;
  expandedSubFolders: Set<string>;
  onToggleSubFolderExpanded: (folderId: string) => void;
  provider: DriveProvider;
  connectionKey?: string;
  depth: number;
}> = ({
  entries,
  folderSelected,
  selectedChildIds,
  onToggleChild,
  expandedSubFolders,
  onToggleSubFolderExpanded,
  provider,
  connectionKey,
  depth,
}) => (
  // ``<ul>`` keeps its implicit ``role="list"`` here — the WAI-ARIA
  // ``role="group"`` for treeview subgroups is conveyed via the parent
  // ``<li role="treeitem" aria-expanded>`` ancestor relationship. Screen
  // readers infer the group from the treeitem hierarchy; we keep the
  // markup linter-clean (biome's ``useSemanticElements`` rule rejects
  // ``role="group"`` on ``<ul>`` and would suggest ``<fieldset>``, which
  // is the wrong semantic for a file picker tree).
  <ul className="flex flex-col gap-0.5">
    {entries.map((entry) =>
      entry.is_folder ? (
        <ChildFolderNode
          key={entry.id}
          folder={entry}
          folderSelected={folderSelected}
          selectedChildIds={selectedChildIds}
          onToggleChild={onToggleChild}
          expandedSubFolders={expandedSubFolders}
          onToggleSubFolderExpanded={onToggleSubFolderExpanded}
          provider={provider}
          connectionKey={connectionKey}
          depth={depth}
        />
      ) : (
        <ChildFileRow
          key={entry.id}
          file={entry}
          folderSelected={folderSelected}
          checked={folderSelected ? true : (selectedChildIds?.files.has(entry.id) ?? false)}
          onToggle={() => onToggleChild(entry.id, false)}
          depth={depth}
        />
      ),
    )}
  </ul>
);

const ChildFileRow: React.FC<{
  file: FolderContentFile;
  folderSelected: boolean;
  checked: boolean;
  onToggle: () => void;
  depth: number;
}> = ({ file, folderSelected, checked, onToggle, depth }) => {
  const { t } = useLingui();
  // June 5 2026 — the BE field is ``sync_status`` (the
  // ``_compute_sync_status`` discriminator), NOT ``status``. The
  // legacy ``TrackedFile.status`` referred to the per-folder
  // ``drive_tracked_files`` table; the per-file delta returned by
  // ``/folder-contents`` is its own enum.
  const isSynced = file.sync_status === "synced";
  return (
    <li
      role="treeitem"
      aria-level={depth + 2}
      aria-selected={checked || undefined}
      style={{ paddingLeft: depth * 14 }}
      className={cn("flex items-center gap-2 px-1 py-1", isSynced && "opacity-60")}
    >
      <Checkbox
        checked={checked}
        disabled={folderSelected || isSynced}
        onChange={onToggle}
        aria-label={t`Includi ${file.name}`}
      />
      {/* Spacer width matches the chevron column on folder rows so
          file + folder names align vertically across the tree. */}
      <span aria-hidden className="inline-block h-3.5 w-3.5 shrink-0" />
      <FileIcon className="h-3.5 w-3.5 text-ink-3" />
      <span className="min-w-0 flex-1 truncate text-[11.5px] text-ink-2">{file.name}</span>
      {isSynced ? (
        <span className="inline-flex items-center gap-1 text-[10px] text-success">
          <CheckCircle2 className="h-3 w-3" /> <Trans>sincronizzato</Trans>
        </span>
      ) : file.sync_status === "modified" ? (
        <span className="text-[10px] text-warning">
          <Trans>Modificato</Trans>
        </span>
      ) : (
        <span className="text-[10px] text-ando-orange">
          <Trans>Nuovo</Trans>
        </span>
      )}
    </li>
  );
};

/**
 * ChildFolderNode — expandable subfolder row inside an already-expanded
 * parent. Owns its own ``expanded`` state and lazy-loads contents on
 * first expand via ``useFolderContents`` — collapsed siblings issue no
 * BE call. Renders a nested ``ChildList`` at ``depth + 1`` for the
 * tree-style indent.
 */
const ChildFolderNode: React.FC<{
  folder: FolderContentFile;
  folderSelected: boolean;
  selectedChildIds: ChildPicks | undefined;
  onToggleChild: (id: string, isFolder: boolean) => void;
  expandedSubFolders: Set<string>;
  onToggleSubFolderExpanded: (folderId: string) => void;
  provider: DriveProvider;
  connectionKey?: string;
  depth: number;
}> = ({
  folder,
  folderSelected,
  selectedChildIds,
  onToggleChild,
  expandedSubFolders,
  onToggleSubFolderExpanded,
  provider,
  connectionKey,
  depth,
}) => {
  const { t } = useLingui();
  // Expansion lives in a Set hoisted to the modal root so collapsing a
  // mid-tree ancestor doesn't unmount the descendants and erase their
  // drill-in state (audit B2). Cheaper than a TanStack Query cache walk
  // — the Set lookup is O(1) per node.
  const expanded = expandedSubFolders.has(folder.id);
  const setExpanded = () => onToggleSubFolderExpanded(folder.id);
  const contents = useFolderContents(expanded ? folder.id : null, provider, connectionKey);
  const isSynced = folder.sync_status === "synced";
  const checked = folderSelected ? true : (selectedChildIds?.folders.has(folder.id) ?? false);

  return (
    <li
      role="treeitem"
      aria-expanded={expanded}
      aria-level={depth + 2}
      aria-selected={checked || undefined}
      style={{ paddingLeft: depth * 14 }}
      className="flex flex-col gap-0.5"
    >
      <div className={cn("flex items-center gap-2 px-1 py-1", isSynced && "opacity-60")}>
        <Checkbox
          checked={checked}
          disabled={folderSelected || isSynced}
          onChange={() => onToggleChild(folder.id, true)}
          aria-label={t`Includi ${folder.name}`}
        />
        <button
          type="button"
          onClick={setExpanded}
          aria-label={expanded ? t`Comprimi` : t`Espandi`}
          aria-expanded={expanded}
          className="inline-flex h-3.5 w-3.5 shrink-0 items-center justify-center text-ink-3 hover:text-ink-1"
        >
          {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
        <Folder className="h-3.5 w-3.5 text-ink-3" strokeWidth={1.75} />
        <span className="min-w-0 flex-1 truncate text-[11.5px] text-ink-1">{folder.name}</span>
        {isSynced ? (
          <span className="inline-flex items-center gap-1 text-[10px] text-success">
            <CheckCircle2 className="h-3 w-3" /> <Trans>sincronizzato</Trans>
          </span>
        ) : folder.sync_status === "modified" ? (
          <span className="text-[10px] text-warning">
            <Trans>Modificato</Trans>
          </span>
        ) : folder.sync_status === "new" ? (
          <span className="text-[10px] text-ando-orange">
            <Trans>Nuovo</Trans>
          </span>
        ) : null}
      </div>
      {expanded ? (
        <div className="ml-[14px] border-hairline/40 border-l pl-2">
          {contents.isLoading ? (
            <p className="py-1 text-[11px] text-ink-3">
              <Trans>Carico…</Trans>
            </p>
          ) : contents.data && contents.data.files.length > 0 ? (
            <ChildList
              entries={contents.data.files}
              // Recursive propagation — if THIS sub-folder is checked
              // (either inherited from a top-level ancestor OR picked
              // explicitly), its descendants are conceptually included.
              // Pre-fix only the top-level ancestor's selection flowed
              // down, so checking a mid-tree sub-folder left its
              // children visually unchecked + enabled — confusing UX.
              folderSelected={folderSelected || checked}
              selectedChildIds={selectedChildIds}
              onToggleChild={onToggleChild}
              expandedSubFolders={expandedSubFolders}
              onToggleSubFolderExpanded={onToggleSubFolderExpanded}
              provider={provider}
              connectionKey={connectionKey}
              depth={depth + 1}
            />
          ) : (
            <p className="py-1 text-[11px] text-ink-3">
              <Trans>Cartella vuota.</Trans>
            </p>
          )}
        </div>
      ) : null}
    </li>
  );
};

const StandaloneFileRow: React.FC<{
  file: TrackedFile;
  selected: boolean;
  onToggle: () => void;
  onDelete: () => void;
}> = ({ file, selected, onToggle, onDelete }) => {
  const { t } = useLingui();
  const isSynced = file.status === "synced";
  return (
    <div
      className={cn(
        "flex items-center gap-2 rounded-md border border-hairline bg-card/50 px-3 py-1.5",
        isSynced && "opacity-60",
      )}
    >
      <Checkbox checked={selected} disabled={isSynced} onChange={onToggle} />
      <FileIcon className="h-3.5 w-3.5 text-ink-3" />
      <span className="min-w-0 flex-1 truncate text-[12px] text-ink-1">{file.name}</span>
      {file.size_bytes ? (
        <span className="shrink-0 text-[10.5px] text-ink-3 tabular-nums">
          {formatBytes(file.size_bytes)}
        </span>
      ) : null}
      <button
        type="button"
        aria-label={t`Rimuovi ${file.name}`}
        onClick={onDelete}
        className="grid h-6 w-6 place-items-center rounded-md text-ink-3 hover:bg-rose-50 hover:text-rose-500"
      >
        <Trash2 className="h-3 w-3" />
      </button>
    </div>
  );
};

const StorageImpactBar: React.FC<{
  used: number;
  projected: number;
  limit: number;
}> = ({ used, projected, limit }) => {
  const pctUsed = Math.min(100, (used / limit) * 100);
  const pctProj = Math.min(100 - pctUsed, (projected / limit) * 100);
  const totalPct = pctUsed + pctProj;
  const tone =
    totalPct >= STORAGE_CRIT_PCT ? "critical" : totalPct >= STORAGE_WARN_PCT ? "warn" : "ok";

  return (
    <section className="rounded-lg border border-hairline bg-cream/40 px-3 py-2">
      <div className="mb-1 flex items-center justify-between">
        <SectionLabel>
          <Trans>Impatto storage</Trans>
        </SectionLabel>
        <span className="text-[10.5px] text-ink-3 tabular-nums">
          {formatBytes(used)} + {formatBytes(projected)} / {formatBytes(limit)}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-ink-3/15">
        <div className="flex h-full">
          <div className="h-full bg-ink-3/50" style={{ width: `${pctUsed}%` }} />
          <div
            className={cn(
              "h-full",
              tone === "critical"
                ? "bg-rose-400"
                : tone === "warn"
                  ? "bg-amber-400"
                  : "bg-ando-orange",
            )}
            style={{ width: `${pctProj}%` }}
          />
        </div>
      </div>
      {tone !== "ok" ? (
        <p
          className={cn(
            "mt-1 inline-flex items-center gap-1 text-[11px]",
            tone === "critical" ? "text-rose-500" : "text-amber-500",
          )}
        >
          <AlertTriangle className="h-3 w-3" />
          {tone === "critical" ? (
            <Trans>Quota quasi piena — riduci la selezione o aumenta il piano.</Trans>
          ) : (
            <Trans>Stai per superare l'80% — considera di ridurre la selezione.</Trans>
          )}
        </p>
      ) : null}
    </section>
  );
};

const DeltaChip: React.FC<{
  tone: "new" | "modified" | "synced";
  children: React.ReactNode;
}> = ({ tone, children }) => (
  <span
    className={cn(
      "inline-flex items-center rounded-full px-1.5 py-0 text-[10px] tabular-nums",
      tone === "new"
        ? "bg-ando-orange-soft text-ando-orange"
        : tone === "modified"
          ? "bg-amber-50 text-amber-600"
          : "bg-success/15 text-success",
    )}
  >
    {children}
  </span>
);

const DeleteConfirm: React.FC<{
  target: { kind: "folder" | "file"; id: string; name: string };
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}> = ({ target, busy, onCancel, onConfirm }) => (
  <div className="rounded-lg border border-warning/40 bg-warning/[0.04] p-4">
    <h3 className="font-semibold text-[13px] text-ink-1">
      <Trans>Rimuovere dal sync?</Trans>
    </h3>
    <p className="mt-1 text-[12px] text-ink-3">
      <Trans>
        "{target.name}" verrà tolto dal tracking. I documenti già indicizzati restano consultabili.
      </Trans>
    </p>
    <div className="mt-3 flex justify-end gap-2">
      <button
        type="button"
        onClick={onCancel}
        className="rounded-md px-3 py-1 font-medium text-[12px] text-ink-2 hover:bg-black/[0.04]"
      >
        <Trans>Annulla</Trans>
      </button>
      <button
        type="button"
        disabled={busy}
        onClick={onConfirm}
        className={cn(
          "rounded-md bg-warning px-3 py-1 font-medium text-[12px] text-white",
          "hover:bg-warning/90 disabled:cursor-not-allowed disabled:opacity-50",
        )}
      >
        {busy ? <Trans>Rimuovendo…</Trans> : <Trans>Rimuovi</Trans>}
      </button>
    </div>
  </div>
);

const EmptyState: React.FC<{ onAdd: () => void; busy: boolean }> = ({ onAdd, busy }) => (
  <div className="flex flex-col items-center gap-3 rounded-lg border border-hairline border-dashed px-4 py-10 text-center">
    <Folder className="h-8 w-8 text-ink-3" strokeWidth={1.5} />
    <div>
      <p className="font-semibold text-[13px] text-ink-1">
        <Trans>Nessun file tracciato ancora</Trans>
      </p>
      <p className="mt-1 text-[12px] text-ink-3">
        <Trans>Aggiungi cartelle o file dal tuo drive per iniziare a sincronizzare.</Trans>
      </p>
    </div>
    <button
      type="button"
      onClick={onAdd}
      disabled={busy}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md bg-ando-orange px-3 py-1.5 font-medium text-[12px] text-white",
        "hover:bg-ando-orange/90 disabled:cursor-not-allowed disabled:opacity-50",
      )}
    >
      {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
      <Trans>Aggiungi</Trans>
    </button>
  </div>
);

// ──────────────────────────────────────────────────────────────────────
// PickedItem → API body mappers
// ──────────────────────────────────────────────────────────────────────

/**
 * Map a ``PickedItem`` to the BE ``FolderSelectionRequest`` row shape.
 * ``folder_path`` is REQUIRED on the BE side; we fall back to the
 * folder name (or id, last resort) so the request never 422s when the
 * picker hands back a folder without an explicit path.
 */
function toSelectFolder(item: PickedItem): {
  folder_id: string;
  folder_path: string;
  folder_name?: string;
  pre_embed?: boolean;
} {
  return {
    folder_id: item.id,
    folder_path: item.folder_path ?? item.name ?? item.id,
    folder_name: item.name,
    pre_embed: true,
  };
}

/**
 * Map a ``PickedItem`` to the BE ``FileSelectionRequest`` row shape.
 * The BE expects ``file_size`` (not ``size_bytes``).
 */
function toSelectFile(item: PickedItem): {
  file_id: string;
  file_name: string;
  mime_type?: string;
  file_size?: number;
  parent_folder_id?: string | null;
} {
  return {
    file_id: item.id,
    file_name: item.name,
    mime_type: item.mime_type,
    file_size: item.size_bytes,
    parent_folder_id: item.parent_folder_id ?? null,
  };
}
